import type { AttributeCatalog } from "../catalog";

/**
 * SL2 attribute catalog, phase 1 — spec §Attribute catalog. `source` is the fixer's starting
 * point in `apps/sl2/wfe/src`, not a verified fact; LOCKER-483 confirms each row or drops it.
 */
export const sl2Catalog = {
  appCode: "sl2",
  attributes: [
    {
      key: "tenant.id",
      type: "string",
      description: { fr: "Identifiant du locataire", en: "Tenant id" },
      source: "current tenant in the store",
    },
    {
      key: "tenant.name",
      type: "string",
      description: { fr: "Nom du locataire", en: "Tenant name" },
      source: "current tenant in the store",
    },
    {
      key: "locker.sstApiVersion",
      type: "enum",
      values: ["v1", "v2"],
      description: { fr: "Version de l'API SST du casier courant", en: "SST API version of the current locker" },
      source: "the current locker (thingsSlice.ts:864); set on locker pages only",
    },
    {
      key: "tenant.features",
      type: "list",
      description: { fr: "Fonctionnalités activées du locataire", en: "Tenant feature flags" },
      source: "tenant feature flags",
    },
    {
      key: "tenant.lockerCount",
      type: "number",
      description: { fr: "Nombre de casiers du locataire", en: "Tenant locker count" },
      source: "things slice",
    },
    {
      key: "user.role",
      type: "enum",
      values: ["operator", "tenantAdmin", "superAdmin"],
      description: { fr: "Rôle de l'utilisateur", en: "User role" },
      source: "/me response",
    },
    {
      key: "user.locale",
      type: "enum",
      values: ["fr", "en"],
      description: { fr: "Langue de l'utilisateur", en: "User language" },
      source: "i18n current language",
    },
    {
      key: "session.mode",
      type: "enum",
      values: ["standalone", "embedded", "RxPro"],
      description: { fr: "Mode de session", en: "Session mode" },
      source: "ModeProvider.tsx",
    },
    {
      key: "page.route",
      type: "string",
      description: { fr: "Route courante", en: "Current route" },
      source: "router location, filled by the shared hook",
    },
    {
      key: "page.uiSurfaces",
      type: "list",
      description: { fr: "Surfaces UI présentes à l'écran", en: "UI surfaces on screen" },
      source: "[data-ui] ids in the DOM, filled by the shared hook",
    },
    {
      key: "device.width",
      type: "number",
      description: { fr: "Largeur de la fenêtre (px)", en: "Window width (px)" },
      source: "window.innerWidth, filled by the shared hook",
    },
  ],
  samples: [
    {
      label: "Operator, v2 locker page, desktop",
      context: {
        "tenant.id": "001tdbwM0000000000000001",
        "tenant.name": "Pharmacie Exemple",
        "locker.sstApiVersion": "v2",
        "tenant.features": ["reports"],
        "tenant.lockerCount": 3,
        "user.role": "operator",
        "user.locale": "fr",
        "session.mode": "standalone",
        "page.route": "/lockers/6571",
        "page.uiSurfaces": ["UI-SL2-001"],
        "device.width": 1440,
      },
    },
    {
      label: "Tenant admin, embedded, tablet",
      context: {
        "tenant.id": "001tdbwM0000000000000002",
        "tenant.name": "Clinique Démo",
        "tenant.features": [],
        "tenant.lockerCount": 12,
        "user.role": "tenantAdmin",
        "user.locale": "en",
        "session.mode": "embedded",
        "page.route": "/reservations",
        "page.uiSurfaces": [],
        "device.width": 800,
      },
    },
    {
      label: "Super admin, v1 locker page, phone",
      context: {
        "tenant.id": "001tdbwM0000000000000003",
        "tenant.name": "Mobilus",
        "locker.sstApiVersion": "v1",
        "tenant.features": ["reports", "olap"],
        "tenant.lockerCount": 140,
        "user.role": "superAdmin",
        "user.locale": "en",
        "session.mode": "RxPro",
        "page.route": "/lockers/1200",
        "page.uiSurfaces": ["UI-SL2-001", "UI-SL2-002"],
        "device.width": 390,
      },
    },
  ],
} as const satisfies AttributeCatalog;
